import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLocale } from "../context/LocaleContext";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useShop, API } from "../context/ShopContext";
import { formatPrice } from "../mock";
import { PageTitle } from "../components/Shared";
import Seo from "../components/Seo";

const MSG = {
  it: {
    checking: "Verifica del pagamento in corso...",
    expired: "La sessione di pagamento è scaduta. Riprova.",
    error: "Errore durante la verifica del pagamento.",
    timeout: "Verifica scaduta. Controlla la tua email per la conferma dell'ordine.",
    invalid: "Sessione di pagamento non valida.",
  },
  en: {
    checking: "Verifying your payment...",
    expired: "The payment session has expired. Please try again.",
    error: "An error occurred while verifying the payment.",
    timeout: "Verification timed out. Please check your email for order confirmation.",
    invalid: "Invalid payment session.",
  },
};

const CheckoutSuccess = () => {
  const { t, lang, clearCart } = useShop();
  const { lp } = useLocale();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error | timeout | invalid
  const [orderInfo, setOrderInfo] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("invalid");
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setState("timeout");
        return;
      }
      attempts += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setOrderInfo(data);
          setState("paid");
          clearCart();
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        console.error("Status poll error", e);
        setState("error");
      }
    };
    poll();
  }, [sessionId, clearCart]);

  const m = MSG[lang];

  return (
    <div data-testid="checkout-success-page">
      <Seo title={t("checkout")} />
      <PageTitle title={t("checkout")} />
      <div className="idm-container idm-order-success">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="idm-spin idm-checking-icon" />
            <h2 data-testid="payment-checking">{m.checking}</h2>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 size={56} strokeWidth={1.3} className="idm-success-icon" />
            <h2 data-testid="order-success-message">{t("orderSuccess")}</h2>
            {orderInfo?.order_id && (
              <p>
                {t("orderNumber")}: <strong data-testid="order-number">{orderInfo.order_id.slice(0, 8).toUpperCase()}</strong>
              </p>
            )}
            {orderInfo?.amount_total != null && (
              <p>
                {t("total")}: <strong>{formatPrice(orderInfo.amount_total / 100)}</strong>
              </p>
            )}
            <div className="idm-success-account" data-testid="success-account-note">
              <p>
                {lang === "it"
                  ? "I file acquistati, gli ordini e gli abbonamenti sono disponibili nella tua Area Clienti. Se \u00e8 il tuo primo acquisto, riceverai un'email per impostare la password di accesso."
                  : "Your purchased files, orders and subscriptions are available in your Customer Area. If this is your first purchase, you will receive an email to set your password."}
              </p>
              <Link to={lp("/account")} className="idm-btn-yellow" style={{ animation: "none" }} data-testid="goto-account-btn">
                {lang === "it" ? "Vai all'Area Clienti" : "Go to Customer Area"}
              </Link>
            </div>
            <Link to={lp("/")} className="idm-btn-dark" data-testid="back-home-btn">
              {t("backToHome")}
            </Link>
          </>
        )}

        {(state === "expired" || state === "error" || state === "timeout" || state === "invalid") && (
          <>
            <XCircle size={56} strokeWidth={1.3} className="idm-error-icon" />
            <h2 data-testid="payment-error-message">{m[state]}</h2>
            <Link to={lp("/checkout")} className="idm-btn-dark" data-testid="back-checkout-btn">
              {t("checkout")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
